import React, {useCallback, useMemo, useRef} from 'react';
import {v4 as uuidv4} from 'uuid';

import {
  CreateStructuredTableProps,
  LastSyncedUpstreamValue,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import computeLSUVs from 'procedures/design/method-config/computeLSUVs';
import {MethodUpdater} from 'procedures/design/ProcedureEditor';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {Method, ProcedureEquipmentSetting} from 'procedures/ProcedureDomainTypes';
import {
  DEFAULT_EQUIPMENT_MODEL,
  DEFAULT_EQUIPMENT_TYPE,
  EquipmentType,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {transformLsuvsForRowFn} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import ProcedureEquipmentTableAdapter, {
  ProcedureEquipmentTableMetadata,
  ProcedureEquipmentTableRowMetadata,
} from 'procedures/tables/ProcedureEquipmentTableAdapter';
import useRowDelegateController from 'procedures/tables/useRowDelegateController';
import {useAllTagSchemasWithStandardShape} from 'procedures/utils/useAllTagSchemas';
import useOnMount from 'ui-library/hooks/useOnMount';

import ProcedureConfigTable from './ProcedureConfigTable';

const baseCreateEquipmentTableMetadata: Omit<
  CreateStructuredTableProps<ProcedureEquipmentTableMetadata>,
  'metadata'
> = {
  name: 'Equipment',
  schemas: [],
  entryId: null,
  selectorsById: {},
  structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
};

function makeEmptyEquipmentSetting(): ProcedureEquipmentSetting {
  return {
    equipmentVersion: {
      cid: uuidv4(),
      id: null,
      procedureEquipmentId: null,
      name: '',
      description: '',
      dataType: DEFAULT_EQUIPMENT_TYPE,
      models: DEFAULT_EQUIPMENT_MODEL,
    },
    values: [],
  };
}

interface EquipmentConfigTableProps {
  method: Method;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  onChangeErrorState: (anyErrors: boolean) => void;
  onReadyForEditing: () => void;
  locked: boolean;
  navigateToStep: (stepCid: string) => void;
}

export default function EquipmentConfigTable(props: EquipmentConfigTableProps): JSX.Element {
  const {method, onChangeMethod, onReadyForEditing} = props;
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();
  const adapter = ProcedureEquipmentTableAdapter.getAdapter();
  const tagSchemas = useAllTagSchemasWithStandardShape(onReadyForEditing);
  const lsuvs = useRef<Record<string, Array<LastSyncedUpstreamValue>> | null>(null);
  const createEquipmentTableMetadata = useMemo(
    () => ({...baseCreateEquipmentTableMetadata, metadata: {navigateToStep: props.navigateToStep}}),
    [props.navigateToStep]
  );

  const rowDelegateController = useRowDelegateController<
    ProcedureEquipmentSetting,
    ProcedureEquipmentTableMetadata,
    ProcedureEquipmentTableRowMetadata,
    EquipmentType
  >({
    dependentColumnProvenanceIds: 'ALL_DELEGATE',
    details: method.equipmentSettings,
    getDataTypeByIndex: (index) =>
      method.equipmentSettings[index]?.equipmentVersion.dataType ?? DEFAULT_EQUIPMENT_TYPE,
    createTable: (installDelegatesIntoRow) => {
      const equipmentSettings =
        method.equipmentSettings.length > 0 ? method.equipmentSettings : [makeEmptyEquipmentSetting()];
      const table = adapter.createTableFromEquipment(
        createEquipmentTableMetadata,
        `procedureEquipmentTable-${method.cid}`,
        equipmentSettings,
        procedureValidationErrors,
        method.parameterUsages
      );

      const rowsWithDelegates = table.rows.map((row, index) => installDelegatesIntoRow(table, row, index));

      if (lsuvs.current == null) {
        lsuvs.current = computeLSUVs(
          rowsWithDelegates,
          table.columns,
          equipmentSettings,
          (e) => e.equipmentVersion.id,
          transformLsuvsForRowFn(
            ProcedureEquipmentTableAdapter.makeLastSyncedUpstreamValues,
            method.parameterUsages,
            (e) => e.equipmentVersion.id
          )
        );
      }
      return {
        ...table,
        rows: table.rows.map((row, index) => {
          const equip = equipmentSettings[index];
          const id = equip.equipmentVersion.id;
          return {
            ...rowsWithDelegates[index],
            ...(id && lsuvs.current![id] ? {lastSyncedUpstreamValues: lsuvs.current![id]} : null),
            isFinalized: id != null,
          };
        }),
      };
    },
    getDelegateColumnByColumnAndDataType:
      ProcedureEquipmentTableAdapter.getDelegateColumnByColumnAndEquipmentType,
    tagSchemas: tagSchemas ?? [],
  });

  const onChangeEquipment = useCallback(
    (equipmentSettings: Array<ProcedureEquipmentSetting>) => {
      onChangeMethod(method.cid, (m) => ({...m, equipmentSettings}));
      // The parent needs to flush before the row delegates can be refreshed
      setTimeout(() => {
        rowDelegateController.onChangeDetails(equipmentSettings);
      }, 0);
    },
    [method, onChangeMethod, rowDelegateController]
  );

  // This table is ready immediately
  useOnMount(props.onReadyForEditing);

  return (
    <ProcedureConfigTable
      tableAdapter={ProcedureEquipmentTableAdapter.getAdapter()}
      createStructuredTableProps={createEquipmentTableMetadata}
      onChangeDetails={onChangeEquipment}
      onChangeErrorState={props.onChangeErrorState}
      rowToDetail={(
        row: StructuredTableRow<ProcedureEquipmentTableRowMetadata>,
        columns: Array<StructuredTableColumn>
      ) => ProcedureEquipmentTableAdapter.rowToEquipmentSetting(columns, row, tagSchemas ?? [])}
      table={rowDelegateController.table}
      tableCid={`procedureEquipmentTable-${method.cid}`}
      locked={props.locked}
      dataTestAttrs={{component: 'EquipmentConfigTable', element: 'root'}}
    />
  );
}
